import { useEffect } from 'react';
import { AppBar, Box, Button, CircularProgress, Container, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { alPerderSesion, api } from './lib/api.js';
import { conectarEventos } from './lib/eventos.js';
import { CLAVE_COLA } from './components/ColaConversion.js';
import { Login } from './pages/Login.js';
import { Avisos } from './components/Avisos.js';
import { EstadoConexion } from './components/EstadoConexion.js';
import { Ajustes } from './pages/Ajustes.js';
import { Buscar } from './pages/Buscar.js';
import { Conversiones } from './pages/Conversiones.js';
import { Descargas } from './pages/Descargas.js';
import { Ficha } from './pages/Ficha.js';
import { Watchlist } from './pages/Watchlist.js';
import { Biblioteca } from './pages/Biblioteca.js';
import { Red } from './pages/Red.js';

const SECCIONES = [
  { to: '/buscar', label: 'Buscar' },
  { to: '/descargas', label: 'Descargas' },
  { to: '/conversiones', label: 'Conversiones' },
  { to: '/watchlist', label: 'Watchlist' },
  { to: '/biblioteca', label: 'Biblioteca' },
  { to: '/red', label: 'Red' },
  { to: '/ajustes', label: 'Ajustes' },
];

export function App() {
  const { pathname } = useLocation();
  const activa = SECCIONES.find((s) => pathname.startsWith(s.to))?.to ?? false;
  const qc = useQueryClient();

  const sesion = useQuery({
    queryKey: ['sesion'],
    queryFn: api.me,
    retry: false,
    staleTime: 60_000,
  });

  // Si cualquier llamada devuelve 401, la sesión ha caducado: se vuelve a la puerta en
  // vez de pintar el fallo como si fuera un error de la pantalla en la que estabas.
  useEffect(() => alPerderSesion(() => void qc.setQueryData(['sesion'], null)), [qc]);

  // UNA conexión para toda la aplicación, aquí y no en cada pantalla.
  //
  // Colgada de Descargas y de Conversiones, cambiar de pestaña cerraba el stream y abría
  // otro, y lo que pasara en ese hueco no lo contaba nadie: los estados solo se movían si
  // estabas mirando la pantalla a la que le tocaba. Se invalidan las tres listas juntas
  // porque un mismo evento las descuadra a la vez —colocar cambia la descarga Y lo que
  // espera en conversiones— y quien las mira ya no es quien recibe el evento.
  const conectado = sesion.data?.authenticated ?? false;
  useEffect(() => {
    if (!conectado) return;
    return conectarEventos(() => {
      void qc.invalidateQueries({ queryKey: ['descargas'] });
      void qc.invalidateQueries({ queryKey: ['conversiones'] });
      void qc.invalidateQueries({ queryKey: CLAVE_COLA });
      // Cuatro, desde que la watchlist enseña en qué punto va la descarga de cada
      // película: sin esto la fila se quedaba en «Descargando» hasta recargar la página.
      // Solo se recarga si alguien está mirando la watchlist —TanStack no refresca las
      // consultas inactivas—, así que no cuesta nada desde las otras pantallas.
      void qc.invalidateQueries({ queryKey: ['watchlist'] });
      // Y la revisión de la biblioteca (D-28), que enseña en qué estado va lo que pidió.
      void qc.invalidateQueries({ queryKey: ['revision'] });
    });
  }, [qc, conectado]);

  const salir = useMutation({
    mutationFn: api.logout,
    onSuccess: () => {
      qc.setQueryData(['sesion'], null);
      qc.clear();
    },
  });

  if (sesion.isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!sesion.data?.authenticated) {
    return <Login onEntrar={() => void qc.invalidateQueries({ queryKey: ['sesion'] })} />;
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <AppBar position="sticky" color="default" elevation={0}>
        {/* A 375 px no caben el nombre, cinco secciones y tres controles en una línea (X-3).
            Encogerlo todo dejaría las pestañas ilegibles y el estado de conexión —que es
            precisamente lo que no se puede esconder— reducido a nada. Así que en móvil el
            nombre se va, los controles se quedan arriba y las pestañas bajan a su propia
            línea, a lo ancho. `order` es lo único que cambia: el marcado es el mismo. */}
        <Toolbar
          sx={{
            gap: { xs: 1, sm: 2 },
            px: { xs: 1, sm: 3 },
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-end', sm: 'flex-start' },
            py: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'primary.main', display: { xs: 'none', md: 'block' } }}
          >
            film-downloader
          </Typography>
          <Tabs
            value={activa}
            variant="scrollable"
            scrollButtons={false}
            sx={{
              order: { xs: 1, sm: 0 },
              width: { xs: '100%', sm: 'auto' },
              flexGrow: { sm: 1 },
              minWidth: 0,
              minHeight: 40,
            }}
          >
            {SECCIONES.map((s) => (
              <Tab
                key={s.to}
                value={s.to}
                label={s.label}
                component={Link}
                to={s.to}
                sx={{ minWidth: { xs: 0, sm: 90 }, px: { xs: 1.5, sm: 2 }, minHeight: 40 }}
              />
            ))}
          </Tabs>
          <EstadoConexion />
          <Avisos />
          <Button size="small" color="inherit" onClick={() => salir.mutate()}>
            Salir
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Routes>
          <Route path="/" element={<Navigate to="/buscar" replace />} />
          <Route path="/buscar" element={<Buscar />} />
          <Route path="/pelicula/:id" element={<Ficha />} />
          {/* La misma ficha: qué es lo dice la propia ficha (D-27), pero la dirección lo
              dice antes, que es lo que se ve al enlazarla o al volver atrás. */}
          <Route path="/serie/:id" element={<Ficha />} />
          <Route path="/descargas" element={<Descargas />} />
          <Route path="/conversiones" element={<Conversiones />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/biblioteca" element={<Biblioteca />} />
          <Route path="/red" element={<Red />} />
          <Route path="/ajustes" element={<Ajustes />} />
        </Routes>
      </Container>
    </Box>
  );
}
